import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Clock, Info, X } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { QuoteTypeBadge } from "@/components/shared/QuoteTypeBadge";
import Services from "@/pages/Services";
import { api, money } from "@/lib/api";
import { CATEGORIES, CATEGORY_PHOTO, CATEGORY_TITLE } from "@/lib/constants";

const priceText = (p = {}) => {
  if (p.type === "instant") return `From ${money(p.unit_price)}`;
  if (p.type === "range") return `${money(p.min_price)} – ${money(p.max_price)}`;
  return "Priced after inspection";
};

const Detail = ({ slug }) => {
  const [svc, setSvc] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data.service)).catch(() => setMissing(true));
  }, [slug]);
  if (missing) return <PageShell title="Service not found" testId="service-not-found"><Link to="/services" className="underline">Back to services</Link></PageShell>;
  if (!svc) return <PageShell testId="service-loading" />;
  return (
    <PageShell testId="service-detail-page" overline={CATEGORY_TITLE[svc.category]} title={svc.title} intro={svc.summary} image={CATEGORY_PHOTO[svc.category]}>
      <nav className="text-sm text-[#56585E] mb-8" data-testid="service-breadcrumb">
        <Link to="/services" className="hover:text-[#F86900]">Services</Link> / <Link to={`/services/${svc.category}`} className="hover:text-[#F86900]">{CATEGORY_TITLE[svc.category]}</Link> / <span className="text-[#0D141A]">{svc.title}</span>
      </nav>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="grid sm:grid-cols-2 gap-5">
            <section className="rounded-2xl bg-white p-6">
              <h2 className="font-display font-bold text-lg">What's included</h2>
              <ul className="mt-4 space-y-2 text-sm">{(svc.inclusions || []).map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-[#027A48] shrink-0 mt-0.5" />{x}</li>)}</ul>
            </section>
            <section className="rounded-2xl bg-white p-6">
              <h2 className="font-display font-bold text-lg">Not included</h2>
              <ul className="mt-4 space-y-2 text-sm">{(svc.exclusions || []).map((x) => <li key={x} className="flex gap-2"><X className="h-4 w-4 text-[#B54708] shrink-0 mt-0.5" />{x}</li>)}</ul>
            </section>
          </div>
          {svc.addons?.length > 0 && (
            <section className="rounded-2xl bg-white p-6" data-testid="service-addons">
              <h2 className="font-display font-bold text-lg">Optional add-ons</h2>
              <div className="mt-4 divide-y">
                {svc.addons.map((a) => (
                  <div key={a.slug} className="flex justify-between py-3 text-sm"><span>{a.label}</span><span className="font-semibold">{a.price ? money(a.price) : "Quoted"}</span></div>
                ))}
              </div>
            </section>
          )}
          {svc.assessment_reasons?.length > 0 && (
            <section className="rounded-2xl border border-[#F86900]/30 bg-[#F86900]/5 p-6 flex gap-3">
              <Info className="h-5 w-5 text-[#B54708] shrink-0" />
              <div className="text-sm"><p className="font-semibold">Why this needs an assessment</p>{svc.assessment_reasons.map((r) => <p key={r} className="mt-1 text-[#56585E]">{r}</p>)}</div>
            </section>
          )}
        </div>
        <aside className="rounded-2xl bg-[#020165] text-white p-6 h-fit lg:sticky lg:top-6" data-testid="service-pricing-box">
          <QuoteTypeBadge type={svc.pricing?.type} />
          <p className="mt-4 text-sm text-white/60">Sample pricing</p>
          <p className="font-display text-3xl font-extrabold" data-testid="service-price-text">{priceText(svc.pricing)}</p>
          <p className="mt-3 flex items-center gap-2 text-sm text-white/75"><Clock className="h-4 w-4" />{svc.duration_label}</p>
          <p className="mt-4 text-xs text-white/50">{svc.price_label} Vehicle size and add-ons change the final figure.</p>
          <Link to={`/quote?service=${svc.slug}`} data-testid="service-detail-quote-cta" className="mt-6 block text-center rounded-full bg-[#F86900] text-[#0D141A] font-bold py-3 hover:bg-[#ff7d1f] transition-colors">Get a quote for my car</Link>
          <Link to={`/book?service=${svc.slug}`} data-testid="service-detail-book-cta" className="mt-3 block text-center rounded-full border border-white/30 font-semibold py-3 hover:bg-white/10 transition-colors">Book this service</Link>
          <Link to="/contact" data-testid="service-detail-contact-cta" className="mt-3 block text-center text-sm text-white/70 hover:text-[#F86900]">Speak with a technician</Link>
        </aside>
      </div>
    </PageShell>
  );
};

export default function ServiceDetail() {
  const { slug } = useParams();
  if (CATEGORIES.some((c) => c.key === slug)) return <Services fixedCategory={slug} />;
  return <Detail slug={slug} />;
}
